import { getEventDesktop } from "app/api/eventsApi";
import { OpenBetsHelper } from "app/helpers/openBetsHelper";
import type { ReportBetMobileType } from "app/types/reportType";
import { ResponsiveScreenSizesInt, SportsNameType } from "app/utils/const";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import DisplayReportHeader from "./DisplayReportHeader/DisplayReportHeader";
import "./Report.scss";

const BetsReportContent = React.lazy(
  () => import("./OpenBetsReportsDesktop/OpenBetsReportContent")
);

type Props = {
  key: string;
  bet: ReportBetMobileType;
  marketName: string;
  eventName: string;
  contentType: string;
  setPositionMatrixModalItem: (...args: Array<any>) => any;
  openPositionMatrixModal: (...args: Array<any>) => any;
  setPositionMatrixModalEvent: (...args: Array<any>) => any;
  translatedBetType: string;
  betType: string;
};

const Report: React.FC<Props> = ({
  key,
  bet,
  marketName,
  eventName,
  contentType,
  setPositionMatrixModalItem,
  openPositionMatrixModal,
  setPositionMatrixModalEvent,
  translatedBetType,
  betType,
}) => {
  const [placedScore, setPlacedScore] = useState<string | null>(null);
  const [periodScore, setPeriodScore] = useState<string | null>(null);
  const [finalScore, setFinalScore] = useState<string | null>(null);

  useEffect(() => {
    const isMobile = window.innerWidth <= ResponsiveScreenSizesInt.mobile;
    const sportName = bet?.sportName;

    // Adding this check but tbh not sure why the postion matrix is being set here in this way..
    // Leaving it here for now, because i cant figure out if removing it will break something.
    // Position matrix only available on desktop and for soccer
    if (!isMobile && sportName?.toUpperCase() === SportsNameType.SOCCER) {
      getEventDesktop(bet.eventId).then(resp => {
        if (resp.id) {
          setPositionMatrixModalItem(resp.id);
          setPositionMatrixModalEvent(resp);
        }
      });
    }

    // Format placedScore
    if (bet.scoreDetails) {
      const formattedPlacedScore = OpenBetsHelper.formatScores({
        scores: bet.scoreDetails,
        showHomePlayerAsFirst: bet.showHomePlayerAsFirst,
      });
      setPlacedScore(formattedPlacedScore);
    }

    // Format periodScore
    if (bet.periodScoreDetails) {
      const formattedPeriodScore = OpenBetsHelper.formatScores({
        scores: bet.periodScoreDetails,
        showHomePlayerAsFirst: bet.showHomePlayerAsFirst,
      });
      setPeriodScore(formattedPeriodScore);
    }

    // Format finalScore
    if (bet.finalScoreDetails) {
      const formattedFinalScore = OpenBetsHelper.formatScores({
        scores: bet.finalScoreDetails,
        showHomePlayerAsFirst: bet.showHomePlayerAsFirst,
      });
      setFinalScore(formattedFinalScore);
    }
  }, [
    bet,
    bet.eventId,
    bet.scoreDetails,
    bet.periodScoreDetails,
    bet.finalScoreDetails,
    bet.showHomePlayerAsFirst,
    setPositionMatrixModalItem,
    setPositionMatrixModalEvent,
    openPositionMatrixModal,
  ]);

  return (
    <div className={`report ${bet.status}`} key={`report-${key}`}>
      <DisplayReportHeader
        bet={bet}
        contentType={contentType}
        eventName={eventName}
        placedScore={placedScore}
        translatedBetType={translatedBetType}
        betType={betType}
      />
      <React.Suspense fallback={<div className="fallback">loading...</div>}>
        <BetsReportContent
          bet={bet}
          contentType={contentType}
          marketName={marketName}
          profit={Boolean(bet.profit)}
          datePlaced={bet.datePlaced}
          dateSettled={bet.dateSettled}
          placedScore={placedScore}
          periodScore={periodScore}
          finalScore={finalScore}
          openPositionMatrixModal={openPositionMatrixModal}
        />
      </React.Suspense>
    </div>
  );
};

const mapStateToProps = state => ({
  session: state.sessionModel.session,
});

const mapDispatchToProps = dispatch => ({
  setPositionMatrixModalItem: (eventId: number) => {
    dispatch.positionMatrixListModel.setPositionMatrixModalItem({
      eventId: eventId,
    });
  },
  setPositionMatrixModalEvent: (event: any) => {
    dispatch.positionMatrixListModel.setPositionMatrixModalEvent(event);
  },
  openPositionMatrixModal: () => {
    dispatch.positionMatrixListModel.openPositionMatrixModal();
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(Report)) as React.FC<
  Omit<
    Props,
    | "setPositionMatrixModalItem"
    | "setPositionMatrixModalEvent"
    | "openPositionMatrixModal"
  >
>;
